import { createElement, type ComponentType, type ReactNode } from 'react';
import { Download, Edit3, ExternalLink, Eye, FolderInput, Printer, UserPlus, type LucideProps } from 'lucide-react';
import type { OverflowMenuAction } from '../components/symphony/ui';
// `openArtifact` is deliberately NOT imported any more: every navigation this module offers is now a real
// anchor, so nothing here calls `window.open`. `test/doc-viewer-ux.test.ts` pins that.
import { downloadArtifact, downloadRemoteFile } from './artifact-file';
import { extractUrl } from './artifact-image';
import { effectiveContent, parseFileRecordContent } from './file-record';
import { wixelEditUrl } from './wixel-link';
import type { DocViewerKind } from './doc-viewer';
import type { WorkspaceFile } from './workspace-files';

/** Where the action set is being rendered — decides which entries appear:
 *   - `row`     — the file/app row's "..." menu (Files gallery table, cross-workspace Files page). Carries
 *     View, since the row itself doesn't already show the document.
 *   - `viewer`  — the open `DocViewerModal`'s own footer (or the mobile-web standalone page rendering the
 *     same body). No View (it would be redundant with itself).
 *   - `panel`   — the no-viewer fallback (`FileDetailBody`), rendered as inline buttons rather than a menu.
 *     No View (there is no viewer to open) and no Print (nothing paginated is showing). */
export type FileActionContext = 'row' | 'viewer' | 'panel';

export interface FileActionCallbacks {
  /** Opens the in-app viewer for this row. Only rendered when `context === 'row'` and `kind` is non-null. */
  onView?: () => void;
  /** Seeds a chat asking about this file. Always present — Discuss is offered for every kind, including
   *  the no-viewer fallback. */
  onDiscuss: () => void;
  /** Opens the "Move to…" dialog. Row context only — moving a file is a thing you do to a row in a list, not
   *  a thing you do while reading it, and the viewer footer has no list to put it back into. Absent when the
   *  caller cannot move the item (a chat attachment has no folder). */
  onMove?: () => void;
  /**
   * Opens the "who can read this" dialog — the action that GRANTS somebody access.
   *
   * Row context only, and only for a file that has an id. It is deliberately distinct from the older
   * `files.share.*` flow (now "Send a copy"), which hands out a link or mails a copy and grants
   * nobody anything. A file written before ids existed has nothing to grant against, so the caller
   * passes no callback and the entry is omitted rather than rendered dead.
   */
  onShare?: () => void;
  /** Prints the document — offered in BOTH row and viewer contexts (same as Download/Open-in-new-tab),
   *  omitted entirely for the kinds `window.print()` cannot reach (see `PRINTABLE_KINDS`). The row-context
   *  caller is responsible for getting the document on screen first (see `WorkspaceFilesTable`'s wiring). */
  onPrint?: () => void;
}

export interface BuildFileActionsOptions {
  context: FileActionContext;
  t: (key: string, options?: Record<string, unknown>) => string;
  /** The agent's short name, for the "Discuss it with {agentName}" label. */
  agentName: string;
  /** This file's OWN page, already mount-prefixed and new-tab-ready (`fileNewTabLink`), when the caller can
   *  address it. Absent for a chat attachment, which no route of ours can name — see `openInNewTab`. */
  fileHref?: string;
}

const icon = (Glyph: ComponentType<LucideProps>): ReactNode => createElement(Glyph, { size: 16, 'aria-hidden': true });

/** The kinds Print is offered for — i.e. every kind that HAS a printable form. The test is not "is it in our
 *  DOM" but "can we get it onto paper", which is a two-branch answer (see `lib/doc-print.ts`):
 *
 *  - **Our own DOM** — `markdown`, `csv`, `text`, `image`, and the `doc`/`slides` Office kinds (converted to
 *    markdown, so the print block reaches them exactly as it reaches a `.md`). `window.print()` prints these,
 *    with the `@media print` block in `styles/globals.css` isolating the document from the app's chrome and
 *    letting it run to as many pages as it needs.
 *  - **A separate document we can still print** — `pdf`. Its bytes are in an `<embed>` the parent job cannot
 *    paginate, so `PdfDocView` claims the print job and prints the PDF's own document (`printPdfObjectUrl`).
 *    Print used to be withheld here on the grounds that the browser's PDF viewer has its own button — but
 *    that button is inside the embed, so it does not exist in the row menu, and it does not exist at all on
 *    a phone (where the embed is replaced by a hand-off poster).
 *  - `wixel` — a design asset, shown as its preview image in our DOM. That image is what prints; the two
 *    "open in Wixel" links carry `data-print-hide`, since a link is nothing on paper.
 *
 *  Two kinds are deliberately absent, and both absences are about the FILE, not about our plumbing:
 *
 *  - `video` / `audio` — there is no printable form of a recording. Neither native platform prints at all,
 *    so nothing diverges here either.
 *  - `html` — the report is an `<iframe srcDoc sandbox>` with NO `allow-same-origin` (a hard security
 *    requirement pinned by `test/doc-viewer-sandbox.test.ts`), so the parent job renders the frame at its
 *    on-screen box — page one only. Printing it properly means calling `print()` INSIDE that frame, which
 *    needs `allow-modals`, which would also let an agent-authored report raise dialogs attributed to our own
 *    origin. A truncated document and a phishing surface are both worse than the honest route the report
 *    already has: **Open in new tab**, where it is the whole document and the browser's own Print works. */
const PRINTABLE_KINDS: ReadonlySet<DocViewerKind> = new Set<DocViewerKind>([
  'markdown',
  'csv',
  'text',
  'image',
  'doc',
  'slides',
  'pdf',
  'wixel',
]);

/** The "Open in new tab" entry, as a REAL ANCHOR — or nothing, when we have no honest target.
 *
 *  It used to be `onSelect: () => openArtifact(item)`, i.e. `window.open`, which failed four ways: a popup
 *  blocker could swallow it, cmd/ctrl/middle-click could not reach it (there was no href to modify-click), an
 *  inline artifact got a blob URL that `artifact-file.ts` REVOKES after 60s so reloading the tab broke, and a
 *  remote file put its signed WixMP URL into browser history and the referrer.
 *
 *  **"Open in new tab" opens THE FILE, not our page about the file.** Two tiers:
 *
 *   1. **The file's own URL, whenever it has one.** This is what the action says it does. Our own page
 *      renders the *previewer* — the same viewer the user just chose to leave — so pointing a new tab at it
 *      answers a different question than the one asked, and a reader who wants the raw PDF/report gets a
 *      second copy of the reading surface instead. It also means the browser's own PDF/image/video handling,
 *      its Save-as and its Print all work on the real document.
 *   2. **Else our page**, for a file whose body is INLINE (markdown, a CSV written as content) and therefore
 *      has no URL to point at. The page is then the only addressable form of the file — it beats nothing, and
 *      for a `kind === null` inline artifact it at least carries the metadata and Download.
 *
 *  When neither applies the entry is OMITTED rather than rendered dead: an inline file whose page we cannot
 *  name has, literally, nowhere to open. Its previous behaviour was a blob-URL tab that `artifact-file.ts`
 *  revoked 60s later, so a reload of that tab broke — not worth preserving, and Download sits right beside
 *  it. The omission is symmetric across every context, which is the invariant `file-actions.test.ts`
 *  protects.
 *
 *  In practice the entry is present for every file the product can address: every workspace-file surface
 *  passes a `fileHref` (see `fileNewTabLink`), and every chat attachment carries a URL. The one file with
 *  neither is a `vfsPath` attachment, whose `url` is `''` by contract and which no route of ours can name —
 *  a data-contract gap, not an address we could invent. */
function openInNewTab(
  item: WorkspaceFile,
  fileHref: string | undefined,
  label: string,
): OverflowMenuAction | null {
  const action = { key: 'open-new-tab', label, icon: icon(ExternalLink) };
  const remoteUrl = extractUrl(effectiveContent(item));
  if (remoteUrl) return { ...action, href: remoteUrl, external: true, newTab: true };
  if (fileHref) return { ...action, href: fileHref, newTab: true };
  return null;
}

/** The row-level "..." menu's and the viewer footer's action set — ONE builder so the two can never drift
 *  apart (see the `buildFileActions` anti-drift test in `file-actions.test.ts`: for a given kind, the
 *  row-context and viewer-context action sets are IDENTICAL minus the View entry).
 *
 *  `kind === null` is the no-viewer fallback: Download / Open-in-new-tab, then Discuss in its own group.
 *  A real kind adds View (row context only) ahead of Download, and Print (both contexts, for the
 *  `PRINTABLE_KINDS` only) after Download — still followed by Open-in-new-tab, then Discuss in its own group.
 *  `wixel` replaces Download/Open-in-new-tab/Print with Open-in-Wixel / Edit-in-Wixel /
 *  Download-the-preview-image, each omitted when its URL is absent from the header. */
export function buildFileActions(
  item: WorkspaceFile,
  kind: DocViewerKind | null,
  callbacks: FileActionCallbacks,
  { context, t, agentName, fileHref }: BuildFileActionsOptions,
): OverflowMenuAction[][] {
  const discussGroup: OverflowMenuAction[] = [
    { key: 'discuss', label: t('agentCreations.detail.discuss', { agentName }), onSelect: callbacks.onDiscuss },
  ];

  // Offered for EVERY kind, not just `wixel`: a Wixel short link carries no extension, so a file pointing at
  // one classifies by whatever its name/MIME says (or as `null`) and would never reach the branch below.
  const editorUrl = wixelEditUrl(effectiveContent(item));
  const editWixel: OverflowMenuAction | null = editorUrl
    ? { key: 'edit-wixel', label: t('fileActions.editInWixel'), icon: icon(Edit3), href: editorUrl, external: true }
    : null;

  if (kind === 'wixel') {
    const { header } = parseFileRecordContent(effectiveContent(item));
    const primary: OverflowMenuAction[] = [];
    if (context === 'row' && callbacks.onView) {
      primary.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
    }
    if (header?.wixelViewerUrl?.trim()) {
      primary.push({ key: 'open-wixel', label: t('fileActions.openInWixel'), icon: icon(ExternalLink), href: header.wixelViewerUrl.trim(), external: true });
    }
    if (editWixel) {
      primary.push(editWixel);
    }
    if (context === 'row' && callbacks.onShare) {
      primary.push({ key: 'share', label: t('files.access.action'), icon: icon(UserPlus), onSelect: callbacks.onShare });
    }
    if (header?.wixelPreviewUrl?.trim()) {
      const previewUrl = header.wixelPreviewUrl.trim();
      primary.push({
        key: 'download',
        label: t('packs.download'),
        icon: icon(Download),
        onSelect: () => void downloadRemoteFile(previewUrl, item.name),
      });
      // The preview image IS the printable form of a wixel asset, so Print rides with it — and only with it.
      // Without a preview the view renders a placeholder glyph, and a page with a glyph on it is not a print.
      if (callbacks.onPrint) {
        primary.push({ key: 'print', label: t('fileActions.print'), icon: icon(Printer), onSelect: callbacks.onPrint });
      }
    }
    return [primary, discussGroup];
  }

  const newTab = openInNewTab(item, fileHref, t('docViewer.openInNewTab'));

  if (kind === null) {
    const group: OverflowMenuAction[] = [];
    // View is offered here too: a file we cannot PREVIEW still opens the same modal, which says so and
    // carries these same actions. Before, `null` meant "this row does something else entirely".
    if (context === 'row' && callbacks.onView) {
      group.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
    }
    if (editWixel) group.push(editWixel);
    group.push({ key: 'download', label: t('packs.download'), icon: icon(Download), onSelect: () => void downloadArtifact(item) });
    // No Print: there is nothing rendered to print. Move is offered — where a file LIVES has nothing to do
    // with whether we can preview it, and an unpreviewable file is exactly one you want to file away.
    if (context === 'row' && callbacks.onShare) {
      group.push({ key: 'share', label: t('files.access.action'), icon: icon(UserPlus), onSelect: callbacks.onShare });
    }
    if (context === 'row' && callbacks.onMove) {
      group.push({ key: 'move', label: t('files.move.action'), icon: icon(FolderInput), onSelect: callbacks.onMove });
    }
    if (newTab) group.push(newTab);
    return [group, discussGroup];
  }

  const primary: OverflowMenuAction[] = [];
  if (context === 'row' && callbacks.onView) {
    primary.push({ key: 'view', label: t('agentCreations.detail.view'), icon: icon(Eye), onSelect: callbacks.onView });
  }
  if (editWixel) primary.push(editWixel);
  primary.push({ key: 'download', label: t('packs.download'), icon: icon(Download), onSelect: () => void downloadArtifact(item) });
  if (PRINTABLE_KINDS.has(kind) && callbacks.onPrint) {
    primary.push({ key: 'print', label: t('fileActions.print'), icon: icon(Printer), onSelect: callbacks.onPrint });
  }
  if (context === 'row' && callbacks.onShare) {
    primary.push({ key: 'share', label: t('files.access.action'), icon: icon(UserPlus), onSelect: callbacks.onShare });
  }
  if (context === 'row' && callbacks.onMove) {
    primary.push({ key: 'move', label: t('files.move.action'), icon: icon(FolderInput), onSelect: callbacks.onMove });
  }
  if (newTab) primary.push(newTab);

  return [primary, discussGroup];
}
